import { useState, useEffect } from 'react';
import { toast } from '@/components/ui/sonner';
import { BookOpen, Plus, Trash2, Users, Calendar, MapPin, Clock, Phone, Edit2, Mail, Video, Link, Key, User, X, Globe } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const FormationsOnline = ({ user, activeModule = "b2b" }) => {
  const [formations, setFormations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingFormation, setEditingFormation] = useState(null);

  const getDefaultFormationLabel = (type) => {
    if (type === 'safety') return 'Formation Safety';
    if (type === 'amazon') return 'Formation Amazon';
    return 'Autre Formation';
  };
  
  // Formation form data
  const [formData, setFormData] = useState({
    formation_type: 'safety', // 'safety', 'amazon', 'autre'
    formation_label: 'Formation Safety',
    selected_employees: [null], // IDs des employés sélectionnés
    employee_ids: [],
    employee_names: [''],
    employee_emails: [''],
    dates: [''],
    rdv_time: '',
    location: 'en_ligne',
    connection_link: '',
    login_id: '',
    login_password: '',
    custom_text: '',
    send_email: true
  });

  const resetForm = () => {
    setFormData({
      formation_type: 'safety',
      formation_label: 'Formation Safety',
      selected_employees: [null],
      employee_ids: [],
      employee_names: [''],
      employee_emails: [''],
      dates: [''],
      rdv_time: '',
      location: 'en_ligne',
      connection_link: '',
      login_id: '',
      login_password: '',
      custom_text: '',
      send_email: true
    });
    setEditingFormation(null);
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [formationsRes, empRes] = await Promise.all([
        axios.get(`${API}/formations-online`, { withCredentials: true }),
        axios.get(`${API}/users`, { withCredentials: true })
      ]);
      setFormations(formationsRes.data);
      setEmployees(empRes.data.filter(e => e.role === 'employee' && e.active !== false));
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Filtrer les employés valides (ceux avec un nom)
      const validEmployeeIndices = formData.employee_names
        .map((n, i) => n?.trim() ? i : -1)
        .filter(i => i >= 0);
      
      const dataToSend = {
        ...formData,
        formation_label: (formData.formation_label || '').trim() || getDefaultFormationLabel(formData.formation_type),
        employee_names: validEmployeeIndices.map(i => formData.employee_names[i]),
        employee_emails: validEmployeeIndices.map(i => formData.employee_emails[i] || ''),
        employee_ids: validEmployeeIndices.map(i => formData.selected_employees[i] || '').filter(id => id),
        dates: formData.dates.filter(d => d)
      };

      // Validation des champs obligatoires pour formation en ligne avec envoi email
      if (dataToSend.send_email && dataToSend.location === 'en_ligne' && !editingFormation) {
        const missingFields = [];
        if (!dataToSend.connection_link) missingFields.push("Lien de connexion");
        if (!dataToSend.rdv_time) missingFields.push("Heure de formation");
        if (!dataToSend.login_id) missingFields.push("Identifiant de connexion");
        
        // Vérifier qu'au moins un employé a un email
        const employeesWithEmail = dataToSend.employee_emails.filter(e => e?.trim()).length;
        if (employeesWithEmail === 0) {
          missingFields.push("Au moins un participant avec email");
        }
        
        if (missingFields.length > 0) {
          alert(`Champs obligatoires pour formation en ligne:\n${missingFields.join('\n')}`);
          return;
        }
      }

      if (editingFormation) {
        await axios.put(`${API}/formations-online/${editingFormation.formation_id}`, dataToSend, { withCredentials: true });
      } else {
        const response = await axios.post(`${API}/formations-online`, dataToSend, { withCredentials: true });
        if (response.data.emails_sent > 0) {
          alert(`Formation créée! ${response.data.emails_sent} email(s) envoyé(s).`);
        }
      }
      
      fetchAll();
      setShowForm(false);
      resetForm();
    } catch (error) {
      console.error('Failed to save formation:', error);
      alert('Erreur lors de la sauvegarde');
    }
  };

  const handleEdit = (formation) => {
    // Essayer de retrouver les IDs des employés par leur email
    const selectedEmployees = (formation.employee_names || []).map((name, idx) => {
      const email = formation.employee_emails?.[idx];
      const emp = employees.find(e => 
        (email && e.email === email) || 
        `${e.first_name || ''} ${e.last_name || ''}`.trim().toLowerCase() === name.toLowerCase() ||
        e.name?.toLowerCase() === name.toLowerCase()
      );
      return emp?.user_id || null;
    });
    
    setFormData({
      formation_type: formation.formation_type || 'safety',
      formation_label: formation.formation_label || getDefaultFormationLabel(formation.formation_type || 'safety'),
      selected_employees: selectedEmployees.length ? selectedEmployees : [null],
      employee_ids: formation.employee_ids || [],
      employee_names: formation.employee_names?.length ? formation.employee_names : [''],
      employee_emails: formation.employee_emails?.length ? formation.employee_emails : [''],
      dates: formation.dates?.length ? formation.dates : [''],
      rdv_time: formation.rdv_time || '',
      location: formation.location || 'en_ligne',
      connection_link: formation.connection_link || '',
      login_id: formation.login_id || '',
      login_password: formation.login_password || '',
      custom_text: formation.custom_text || '',
      send_email: false
    });
    setEditingFormation(formation);
    setShowForm(true);
  };

  const handleDelete = async (formationId) => {
    if (!window.confirm('Supprimer cette formation ?')) return;
    try {
      await axios.delete(`${API}/formations-online/${formationId}`, { withCredentials: true });
      fetchAll();
    } catch (error) {
      console.error('Failed to delete formation:', error);
      toast.error(`Formation non supprimée : ${typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'vérifiez la connexion puis réessayez'}`);
    }
  };

  // Handlers for dynamic fields
  const handleAddEmployee = () => {
    setFormData(prev => ({
      ...prev,
      selected_employees: [...prev.selected_employees, null],
      employee_names: [...prev.employee_names, ''],
      employee_emails: [...prev.employee_emails, '']
    }));
  };

  const handleRemoveEmployee = (index) => {
    setFormData(prev => ({
      ...prev,
      selected_employees: prev.selected_employees.filter((_, i) => i !== index),
      employee_names: prev.employee_names.filter((_, i) => i !== index),
      employee_emails: prev.employee_emails.filter((_, i) => i !== index)
    }));
  };

  // Sélection d'un employé depuis la liste déroulante
  const handleSelectEmployee = (index, employeeId) => {
    const emp = employees.find(e => e.user_id === employeeId);
    if (emp) {
      const fullName = `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || emp.name;
      setFormData(prev => ({
        ...prev,
        selected_employees: prev.selected_employees.map((id, i) => i === index ? employeeId : id),
        employee_names: prev.employee_names.map((name, i) => i === index ? fullName : name),
        employee_emails: prev.employee_emails.map((email, i) => i === index ? (emp.email || '') : email)
      }));
    } else {
      // Reset si aucun employé sélectionné
      setFormData(prev => ({
        ...prev,
        selected_employees: prev.selected_employees.map((id, i) => i === index ? null : id),
        employee_names: prev.employee_names.map((name, i) => i === index ? '' : name),
        employee_emails: prev.employee_emails.map((email, i) => i === index ? '' : email)
      }));
    }
  };

  const handleAddDate = () => {
    setFormData(prev => ({
      ...prev,
      dates: [...prev.dates, '']
    }));
  };

  const handleRemoveDate = (index) => {
    setFormData(prev => ({
      ...prev,
      dates: prev.dates.filter((_, i) => i !== index)
    }));
  };

  const formatDate = (dateStr) => {
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  };

  const getTypeLabel = (type) => {
    const labels = {
      'safety': 'Formation Safety',
      'amazon': 'Formation Amazon',
      'autre': 'Autre Formation'
    };
    return labels[type] || type;
  };

  const getTypeColor = (type) => {
    const colors = {
      'safety': 'bg-green-100 text-green-700',
      'amazon': 'bg-orange-100 text-orange-700',
      'autre': 'bg-blue-100 text-blue-700'
    };
    return colors[type] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <Layout user={user} activeModule={activeModule}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F172A]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} activeModule={activeModule}>
      <div className="max-w-6xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-purple-100 rounded-xl">
              <Video className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#0F172A]">Formations en ligne</h1>
              <p className="text-sm text-gray-500">Safety, Amazon et autres formations digitales</p>
            </div>
          </div>
          <Button 
            onClick={() => setShowForm(true)}
            className="bg-[#0F172A] hover:bg-[#1e293b]"
            data-testid="new-online-formation-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Nouvelle formation
          </Button>
        </div>

        {/* Formations List */}
        {formations.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Video className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500">Aucune formation en ligne planifiée</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {formations.map((formation) => (
              <Card key={formation.formation_id} className="hover:shadow-lg transition-shadow" data-testid={`formation-card-${formation.formation_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {/* Type and Status */}
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getTypeColor(formation.formation_type)}`}>
                          {formation.formation_label || getTypeLabel(formation.formation_type)}
                        </span>
                        {formation.rdv_time && (
                          <span className="flex items-center gap-1 text-sm text-gray-500">
                            <Clock className="w-3 h-3" /> {formation.rdv_time}
                          </span>
                        )}
                        {formation.location === 'en_ligne' ? (
                          <span className="flex items-center gap-1 text-sm text-purple-600">
                            <Globe className="w-3 h-3" /> En ligne
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-sm text-gray-600">
                            <MapPin className="w-3 h-3" /> {formation.location}
                          </span>
                        )}
                      </div>

                      {/* Connection Link */}
                      {formation.connection_link && (
                        <div className="flex items-center gap-2 mb-2 text-sm text-blue-600">
                          <Link className="w-4 h-4" />
                          <a href={formation.connection_link} target="_blank" rel="noopener noreferrer" className="hover:underline truncate max-w-md">
                            {formation.connection_link}
                          </a>
                        </div>
                      )}

                      {/* Login Info */}
                      {formation.login_id && (
                        <div className="flex items-center gap-4 mb-2 text-sm text-gray-600 bg-yellow-50 p-2 rounded">
                          <span className="flex items-center gap-1">
                            <Key className="w-3 h-3" /> ID: {formation.login_id}
                          </span>
                          {formation.login_password && (
                            <span>MDP: {formation.login_password}</span>
                          )}
                        </div>
                      )}

                      {/* Employees */}
                      <div className="flex flex-wrap gap-2 mb-3">
                        {formation.employee_names?.map((name, idx) => (
                          <div key={idx} className="flex items-center gap-2 bg-purple-50 px-3 py-1.5 rounded-full">
                            <User className="w-4 h-4 text-purple-600" />
                            <span className="text-sm font-medium text-purple-700">{name}</span>
                            {formation.employee_emails?.[idx] && (
                              <span className="text-xs text-purple-500">
                                ({formation.employee_emails[idx]})
                              </span>
                            )}
                          </div>
                        ))}
                      </div>

                      {/* Dates */}
                      <div className="flex flex-wrap gap-2">
                        {formation.dates?.map((date, idx) => (
                          <span key={idx} className="px-2 py-1 bg-gray-100 rounded text-xs text-gray-600">
                            <Calendar className="w-3 h-3 inline mr-1" />
                            {formatDate(date)}
                          </span>
                        ))}
                      </div>

                      {/* Custom Text */}
                      {formation.custom_text && (
                        <div className="mt-2 p-2 bg-gray-50 rounded text-sm text-gray-700 italic">
                          {formation.custom_text}
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(formation)}
                        data-testid={`edit-formation-${formation.formation_id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => handleDelete(formation.formation_id)}
                        data-testid={`delete-formation-${formation.formation_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-4 border-b">
                <h2 className="text-lg font-semibold">
                  {editingFormation ? 'Modifier la formation' : 'Nouvelle formation en ligne'}
                </h2>
                <button
                  onClick={() => {
                    setShowForm(false);
                    resetForm();
                  }}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-4 space-y-4">
                {/* Type de formation */}
                <div>
                  <Label className="font-semibold">Type de formation *</Label>
                  <select
                    value={formData.formation_type}
                    onChange={(e) => {
                      const newType = e.target.value;
                      setFormData(prev => {
                        const prevDefault = getDefaultFormationLabel(prev.formation_type);
                        const shouldAutoReplaceLabel = !prev.formation_label || prev.formation_label === prevDefault;
                        return {
                          ...prev,
                          formation_type: newType,
                          formation_label: shouldAutoReplaceLabel ? getDefaultFormationLabel(newType) : prev.formation_label
                        };
                      });
                    }}
                    required
                    className="w-full mt-1 h-10 px-3 border border-gray-200 rounded-md"
                    data-testid="formation-type-select"
                  >
                    <option value="safety">Formation Safety</option>
                    <option value="amazon">Formation Amazon</option>
                    <option value="autre">Autre Formation</option>
                  </select>
                </div>

                <div>
                  <Label className="font-semibold">Intitulé de formation *</Label>
                  <Input
                    value={formData.formation_label}
                    onChange={(e) => setFormData(prev => ({ ...prev, formation_label: e.target.value }))}
                    placeholder="Ex: Formation Colis Privé"
                    required
                    className="mt-1"
                    data-testid="formation-label-input"
                  />
                </div>

                {/* Lieu */}
                <div>
                  <Label className="font-semibold">Lieu</Label>
                  <Input
                    value={formData.location}
                    onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                    placeholder="'en_ligne' ou adresse physique"
                    className="mt-1"
                  />
                </div>

                {/* Heure */}
                <div>
                  <Label className="font-semibold">Heure</Label>
                  <Input
                    type="time"
                    value={formData.rdv_time}
                    onChange={(e) => setFormData(prev => ({ ...prev, rdv_time: e.target.value }))}
                    className="mt-1"
                  />
                </div>

                {/* Lien de connexion */}
                <div>
                  <Label className="font-semibold">Lien de connexion</Label>
                  <Input
                    value={formData.connection_link}
                    onChange={(e) => setFormData(prev => ({ ...prev, connection_link: e.target.value }))}
                    placeholder="https://zoom.us/... ou https://teams.microsoft.com/..."
                    className="mt-1"
                  />
                </div>

                {/* Identifiants */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="font-semibold text-sm">Identifiant connexion</Label>
                    <Input
                      value={formData.login_id}
                      onChange={(e) => setFormData(prev => ({ ...prev, login_id: e.target.value }))}
                      placeholder="ID de connexion"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="font-semibold text-sm">Mot de passe</Label>
                    <Input
                      value={formData.login_password}
                      onChange={(e) => setFormData(prev => ({ ...prev, login_password: e.target.value }))}
                      placeholder="Mot de passe"
                      className="mt-1"
                    />
                  </div>
                </div>

                {/* Employés */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="font-semibold">Participants</Label>
                    <Button type="button" variant="outline" size="sm" onClick={handleAddEmployee}>
                      <Plus className="w-4 h-4 mr-1" /> Ajouter
                    </Button>
                  </div>
                  {formData.selected_employees.map((selectedId, index) => (
                    <div key={index} className="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-medium text-gray-500">Participant {index + 1}</span>
                        {formData.selected_employees.length > 1 && (
                          <Button type="button" variant="ghost" size="sm" onClick={() => handleRemoveEmployee(index)} className="ml-auto">
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                      
                      {/* Liste déroulante des employés */}
                      <select
                        value={selectedId || ''}
                        onChange={(e) => handleSelectEmployee(index, e.target.value)}
                        className="w-full h-10 px-3 border border-gray-300 rounded-md bg-white mb-2"
                        data-testid={`employee-select-${index}`}
                      >
                        <option value="">-- Sélectionner un employé --</option>
                        {employees
                          .filter(emp => emp.status === 'approved')
                          .sort((a, b) => {
                            const nameA = `${a.first_name || ''} ${a.last_name || ''}`.trim() || a.name;
                            const nameB = `${b.first_name || ''} ${b.last_name || ''}`.trim() || b.name;
                            return nameA.localeCompare(nameB);
                          })
                          .map(emp => {
                            const fullName = `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || emp.name;
                            return (
                              <option key={emp.user_id} value={emp.user_id}>
                                {fullName} {emp.email ? `(${emp.email})` : ''}
                              </option>
                            );
                          })}
                      </select>
                      
                      {/* Infos de l'employé sélectionné (lecture seule) */}
                      {selectedId && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Mail className="w-3 h-3 text-purple-500" />
                          <span>{formData.employee_emails[index] || 'Pas d\'email'}</span>
                        </div>
                      )}
                      
                      {/* Avertissement si pas d'email */}
                      {selectedId && !formData.employee_emails[index] && formData.send_email && (
                        <p className="text-xs text-orange-600 mt-2">
                          ⚠️ Cet employé n'a pas d'email enregistré
                        </p>
                      )}
                    </div>
                  ))}
                  
                  {formData.send_email && formData.employee_emails.filter(e => e?.trim()).length === 0 && (
                    <p className="text-xs text-red-600 mt-1 p-2 bg-red-50 rounded">
                      ⚠️ Aucun participant avec email sélectionné - Les convocations ne pourront pas être envoyées
                    </p>
                  )}
                </div>

                {/* Dates */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="font-semibold">Dates *</Label>
                    <Button type="button" variant="outline" size="sm" onClick={handleAddDate}>
                      <Plus className="w-4 h-4 mr-1" /> Ajouter
                    </Button>
                  </div>
                  {formData.dates.map((date, index) => (
                    <div key={index} className="flex gap-2 mb-2">
                      <Input
                        type="date"
                        value={date}
                        onChange={(e) => {
                          const newDates = [...formData.dates];
                          newDates[index] = e.target.value;
                          setFormData(prev => ({ ...prev, dates: newDates }));
                        }}
                        className="flex-1"
                        required={index === 0}
                      />
                      {formData.dates.length > 1 && (
                        <Button type="button" variant="outline" size="sm" onClick={() => handleRemoveDate(index)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>

                {/* Texte personnalisé */}
                <div>
                  <Label className="font-semibold">Instructions personnalisées</Label>
                  <textarea
                    value={formData.custom_text}
                    onChange={(e) => setFormData(prev => ({ ...prev, custom_text: e.target.value }))}
                    placeholder="Instructions spécifiques pour cette formation..."
                    className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-md text-sm"
                    rows={3}
                  />
                </div>

                {/* Envoi email */}
                <div className="flex items-center gap-2 p-3 bg-purple-50 rounded-lg">
                  <input
                    type="checkbox"
                    id="send_email_online"
                    checked={formData.send_email}
                    onChange={(e) => setFormData(prev => ({ ...prev, send_email: e.target.checked }))}
                    className="w-4 h-4"
                  />
                  <label htmlFor="send_email_online" className="text-sm">
                    <Mail className="w-4 h-4 inline mr-1 text-purple-600" />
                    Envoyer automatiquement un email aux participants
                  </label>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button type="submit" className="flex-1 bg-[#0F172A]" data-testid="submit-formation-btn">
                    {editingFormation ? 'Mettre à jour' : 'Créer la formation'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default FormationsOnline;
